import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  deleteManualTransaction,
  getManualTransactions,
  ManualTransaction,
} from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { formatCrypto, formatDate, formatUsd } from './taxReportingUtils';
import DataTable, { DataTableFilter } from '../shared/DataTable';
import { usePortfolioRegistry } from '../../hooks/usePortfolioRegistry';
import ManualTransactionModal from './ManualTransactionModal';
import { InboxProvenanceBadge } from './InboxProvenanceBadge';
import './ManualTransactionsView.css';

// =============================================================================
// Manual Transactions list view — server-paginated CRUD table.
//
// Mirrors the server-paginated idiom established in CoinbaseTransactionsView.tsx:
// - pageIndex/pageSize/sorting in local state, threaded into both the query
//   params and the React Query queryKey.
// - sort change resets pageIndex to 0.
// - filter change resets pageIndex to 0.
// - useAutoRefreshTrigger('tax-reporting') drives reactive refetch.
//
// Differs from read-only views by adding:
// - "New Manual Transaction" button (opens ManualTransactionModal in create mode).
// - Per-row Edit/Delete actions (Edit reopens the modal in edit mode; Delete
//   opens an inline confirmation panel that requires a reason).
// - Mutations invalidate ['tax', 'manual-transactions'] on success so the
//   table refetches against the latest server state.
// =============================================================================

type ModalState =
  | { mode: 'create' }
  | { mode: 'edit'; record: ManualTransaction }
  | null;

export const ManualTransactionsView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');
  const queryClient = useQueryClient();

  const [portfolioId, setPortfolioId] = useState<string>('');
  const [tradeSide, setTradeSide] = useState<string>('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'transaction_date', desc: true },
  ]);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0]
    ? sorting[0].desc
      ? 'desc'
      : 'asc'
    : undefined;

  const [modalState, setModalState] = useState<ModalState>(null);
  const [deleteItem, setDeleteItem] = useState<ManualTransaction | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPageIndex(0);
  };

  const params = {
    taxYear,
    portfolioId: portfolioId || undefined,
    tradeSide: tradeSide || undefined,
    sortBy,
    sortDir,
    page: pageIndex + 1,
    pageSize,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'tax',
      'manual-transactions',
      taxYear,
      portfolioId,
      tradeSide,
      sortBy,
      sortDir,
      pageIndex,
      pageSize,
      refreshTrigger,
    ],
    queryFn: () => getManualTransactions(params),
  });

  const items = data?.items ?? [];
  const total = data?.metadata.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;

  const { options: portfolioOptions } = usePortfolioRegistry();

  const deleteMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      deleteManualTransaction(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax', 'manual-transactions'] });
      setDeleteItem(null);
      setDeleteReason('');
      setDeleteError(null);
    },
    onError: (err: unknown) => {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete');
    },
  });

  const columns = useMemo<ColumnDef<ManualTransaction>[]>(
    () => [
      {
        accessorKey: 'transaction_date',
        id: 'transaction_date',
        header: 'Date',
        cell: (info) => (
          <span className="date-cell">{formatDate(info.getValue() as string)}</span>
        ),
      },
      { accessorKey: 'portfolio_id', id: 'portfolio_id', header: 'Portfolio' },
      { accessorKey: 'product_id', id: 'product_id', header: 'Product' },
      {
        accessorKey: 'trade_side',
        id: 'trade_side',
        header: 'Side',
        cell: (info) => {
          const side = info.getValue() as string | undefined;
          if (!side) return null;
          return (
            <span className={`badge badge-${side.toLowerCase()}`}>{side}</span>
          );
        },
      },
      {
        accessorKey: 'quantity',
        id: 'quantity',
        header: 'Quantity',
        cell: (info) => formatCrypto(info.getValue() as string),
      },
      {
        accessorKey: 'price_per_unit',
        id: 'price_per_unit',
        header: 'Price',
        cell: (info) => (
          <span className="price-cell">{formatUsd(info.getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'total_value',
        id: 'total_value',
        header: 'Total',
        cell: (info) => formatUsd(info.getValue() as string),
      },
      {
        accessorKey: 'fees',
        id: 'fees',
        header: 'Fees',
        cell: (info) => (
          <span className="fee-cell">{formatUsd(info.getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'description',
        id: 'description',
        header: 'Description',
        enableSorting: false,
      },
      {
        accessorKey: 'source_reference',
        id: 'source_reference',
        header: 'Reference',
        cell: (info) => {
          const ref = info.getValue() as string | undefined;
          if (ref && ref.startsWith('inbox_resolution:')) {
            return (
              <InboxProvenanceBadge
                sourceReference={ref}
                occurrenceDate={info.row.original.transaction_date}
                productId={info.row.original.product_id}
              />
            );
          }
          return ref ?? '—';
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="manual-tx-actions">
              <button
                type="button"
                className="modal-pill-button modal-pill-preview"
                onClick={() => setModalState({ mode: 'edit', record: row })}
                aria-label={`Edit transaction ${row.transaction_id}`}
              >
                Edit
              </button>
              <button
                type="button"
                className="modal-pill-button modal-pill-delete"
                onClick={() => {
                  setDeleteItem(row);
                  setDeleteReason('');
                  setDeleteError(null);
                }}
                aria-label={`Delete transaction ${row.transaction_id}`}
              >
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  const filters: DataTableFilter[] = [
    {
      id: 'portfolio_id',
      label: 'Portfolio',
      value: portfolioId,
      options: portfolioOptions,
      onChange: onFilterChange(setPortfolioId),
    },
    {
      id: 'trade_side',
      label: 'Side',
      value: tradeSide,
      options: [
        { value: 'BUY', label: 'BUY' },
        { value: 'SELL', label: 'SELL' },
      ],
      onChange: onFilterChange(setTradeSide),
    },
  ];

  return (
    <div aria-label="Manual Transactions view" className="manual-tx-view">
      <DataTable<ManualTransaction>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            setPageIndex(0);
          },
        }}
        filters={filters}
        headerActions={
          <button
            type="button"
            className="modal-pill-button modal-pill-primary"
            onClick={() => setModalState({ mode: 'create' })}
          >
            New Manual Transaction
          </button>
        }
        loadingMessage="Loading manual transactions…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={
          error instanceof Error ? error.message : 'Failed to load manual transactions'
        }
        emptyMessage="No manual transactions for these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />

      {modalState && (
        <ManualTransactionModal
          mode={modalState.mode}
          initial={modalState.mode === 'edit' ? modalState.record : undefined}
          onClose={() => setModalState(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tax', 'manual-transactions'] });
            setModalState(null);
          }}
        />
      )}

      {deleteItem && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!deleteMutation.isPending) {
              setDeleteItem(null);
              setDeleteReason('');
              setDeleteError(null);
            }
          }}
        >
          <div
            className="modal-content manual-tx-delete-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Delete Manual Transaction</h2>
              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setDeleteItem(null);
                  setDeleteReason('');
                  setDeleteError(null);
                }}
                disabled={deleteMutation.isPending}
                aria-label="Close delete confirmation"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                You are about to delete manual transaction #{deleteItem.transaction_id}
                {' '}({deleteItem.trade_side} {formatCrypto(deleteItem.quantity)}{' '}
                {deleteItem.product_id} on {formatDate(deleteItem.transaction_date)}). This
                action soft-deletes the record and is logged in the audit trail.
              </p>
              <div className="form-group">
                <label htmlFor="delete-reason">
                  Reason <span className="required">*</span>
                </label>
                <input
                  id="delete-reason"
                  type="text"
                  className="form-input"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  disabled={deleteMutation.isPending}
                  placeholder="Why is this being deleted?"
                />
              </div>
              {deleteError && <div className="form-error" role="alert">{deleteError}</div>}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="modal-pill-button modal-pill-cancel"
                onClick={() => {
                  setDeleteItem(null);
                  setDeleteReason('');
                  setDeleteError(null);
                }}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-pill-button modal-pill-delete"
                onClick={() => {
                  if (!deleteReason.trim()) {
                    setDeleteError('Reason is required');
                    return;
                  }
                  deleteMutation.mutate({
                    id: deleteItem.transaction_id,
                    reason: deleteReason.trim(),
                  });
                }}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualTransactionsView;
